import { useState } from 'react';
import { toast } from './Toast';
import {
  Sparkles, Loader2, X, Copy, Send, Image as ImageIcon, FileText, Mail,
  Megaphone, Newspaper, Target, Download, Check, ChevronDown, ExternalLink
} from 'lucide-react';

const MATERIAL_TYPES = [
  { id: 'social_post', label: 'Post Social Media', icon: Megaphone, color: 'bg-blue-500' },
  { id: 'promotional_flyer', label: 'Ulotka Promocyjna', icon: FileText, color: 'bg-pink-500' },
  { id: 'offer_document', label: 'Oferta / Wycena', icon: Target, color: 'bg-emerald-500' },
  { id: 'email_campaign', label: 'Kampania Email', icon: Mail, color: 'bg-orange-500' },
  { id: 'case_study', label: 'Case Study', icon: Newspaper, color: 'bg-purple-500' },
  { id: 'google_ad', label: 'Reklama Google Ads', icon: Target, color: 'bg-red-500' },
  { id: 'newsletter_section', label: 'Sekcja Newslettera', icon: Mail, color: 'bg-cyan-500' },
];

const PRODUCT_LABELS: Record<string, string> = {
  pup_grant: 'Dotacja PUP', wsparcie_na_starcie: 'Wsparcie na Starcie',
  kfs: 'KFS Szkolenia', dotacje_rozwojowe: 'Dotacje Rozwojowe',
  turnusy: 'Turnusy Rehabilitacyjne', wycieczki: 'eNHa TRIP',
  zus: 'Pomoc ZUS', bhp: 'Szkolenia BHP', czyste_powietrze: 'Czyste Powietrze',
  hutnik_biznes: 'Hutnik Business', marketing_beauty: 'Smart Beauty',
  gastro_consulting: 'Gastro Consulting', uslugi_ksiegowe: 'Usługi Księgowe',
  granty_fpn: 'Granty FPN', pozyczki_rozwojowe: 'Pożyczki Rozwojowe',
};

interface Props {
  applicationId?: number;
  productCode?: string;
  onClose: () => void;
  onPublishToSocial?: (content: string, imageUrl?: string) => void;
}

interface GeneratedMaterial {
  type: string;
  label: string;
  data: any;
  image_url: string | null;
  generated_at: string;
}

export function MarketingGenerator({ applicationId, productCode, onClose, onPublishToSocial }: Props) {
  const [selectedType, setSelectedType] = useState('social_post');
  const [customContext, setCustomContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<GeneratedMaterial | null>(null);
  const [copied, setCopied] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(true);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const res = await fetch('/api/social/marketing/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId, materialType: selectedType, productCode, customContext: customContext || undefined }),
      });
      if (!res.ok) throw new Error('Błąd generowania');
      const data = await res.json();
      setResult(data);
      setShowTypeSelector(false);
    } catch (err: any) {
      toast.error('Błąd: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getMainContent = (data: any, type: string): string => {
    if (type === 'social_post') return `${data.post || ''}\n\n${(data.hashtags || []).join(' ')}`;
    if (type === 'promotional_flyer') return `${data.headline}\n${data.subtitle}\n\n${(data.benefits || []).map((b: string) => `• ${b}`).join('\n')}\n\n${data.cta}`;
    if (type === 'offer_document') return `${data.title}\n\n${data.intro}\n\nZakres:\n${(data.scope || []).map((s: string) => `• ${s}`).join('\n')}\n\n${data.pricing || ''}\n\n${data.terms || ''}`;
    if (type === 'email_campaign') return `Temat: ${data.subject}\n\n${data.headline}\n\n${(data.body_paragraphs || []).join('\n\n')}\n\n[${data.cta_text}]`;
    if (type === 'case_study') return `${data.title}\n\nWyzwanie: ${data.challenge}\n\nRozwiązanie: ${data.solution}\n\nRezultaty:\n${(data.results || []).map((r: string) => `• ${r}`).join('\n')}\n\n"${data.quote}" — ${data.quote_author}`;
    if (type === 'google_ad') return (data.variants || []).map((v: any, i: number) => `Wariant ${i + 1}:\nH1: ${v.h1}\nH2: ${v.h2}\nH3: ${v.h3}\nOpis 1: ${v.d1}\nOpis 2: ${v.d2}`).join('\n\n');
    if (type === 'newsletter_section') return `${data.headline}\n\n${data.lead}\n\n${data.body}\n\n${data.cta_text}`;
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0f2a4d] via-[#1a3d6e] to-[#d4a017] text-white p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Sparkles size={22} />
              Generator Materiałów Marketingowych
            </h2>
            <p className="text-sm text-blue-200 mt-0.5">
              {productCode ? PRODUCT_LABELS[productCode] || productCode : 'Wszystkie produkty'}
              {applicationId ? ` — wniosek #${applicationId}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Type Selector */}
          {showTypeSelector && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Wybierz typ materiału</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {MATERIAL_TYPES.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setSelectedType(t.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        selectedType === t.id
                          ? 'border-[#0f2a4d] bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-9 h-9 ${t.color} rounded-lg flex items-center justify-center mb-2`}>
                        <Icon size={18} className="text-white" />
                      </div>
                      <span className="text-sm font-medium text-gray-800">{t.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Custom context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dodatkowy kontekst (opcjonalnie)</label>
                <textarea
                  value={customContext}
                  onChange={e => setCustomContext(e.target.value)}
                  className="w-full p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Np. promocja wiosenna, target 25-35 lat, budżet 5000 PLN..."
                />
              </div>

              <button
                onClick={generate}
                disabled={generating}
                className="w-full bg-gradient-to-r from-[#0f2a4d] to-[#d4a017] text-white py-3.5 rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
              >
                {generating ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
                {generating ? 'AI generuje materiał...' : 'Generuj materiał'}
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-lg">{result.label}</h3>
                <button onClick={() => { setResult(null); setShowTypeSelector(true); }} className="text-sm text-blue-600 hover:underline">
                  Generuj nowy
                </button>
              </div>

              {/* Image preview */}
              {result.image_url && (
                <div className="relative rounded-xl overflow-hidden border shadow-sm">
                  <img src={result.image_url} alt="Wygenerowany obraz" className="w-full max-h-[400px] object-cover" />
                  <div className="absolute top-3 right-3 flex gap-2">
                    <a href={result.image_url} download className="bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow hover:bg-white transition">
                      <Download size={16} className="text-gray-700" />
                    </a>
                  </div>
                </div>
              )}

              {/* Content preview card */}
              <div className="bg-gray-50 rounded-xl border p-5 space-y-3">
                {result.type === 'social_post' && result.data.post && (
                  <SocialPostPreview data={result.data} imageUrl={result.image_url} />
                )}
                {result.type === 'promotional_flyer' && (
                  <FlyerPreview data={result.data} imageUrl={result.image_url} />
                )}
                {result.type === 'offer_document' && (
                  <OfferPreview data={result.data} />
                )}
                {result.type === 'email_campaign' && (
                  <EmailPreview data={result.data} />
                )}
                {result.type === 'case_study' && (
                  <CaseStudyPreview data={result.data} />
                )}
                {result.type === 'google_ad' && (
                  <GoogleAdPreview data={result.data} />
                )}
                {result.type === 'newsletter_section' && (
                  <NewsletterPreview data={result.data} />
                )}
              </div>

              {/* Platform tips */}
              {result.type === 'social_post' && result.data.platform_tips && (
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(result.data.platform_tips as Record<string, string>).map(([platform, tip]) => (
                    <div key={platform} className="bg-white rounded-lg border p-3">
                      <span className="text-xs font-bold text-gray-500 uppercase">{platform}</span>
                      <p className="text-xs text-gray-600 mt-1">{tip}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  onClick={() => copyToClipboard(getMainContent(result.data, result.type))}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm font-medium"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Skopiowano!' : 'Kopiuj treść'}
                </button>

                {onPublishToSocial && result.type === 'social_post' && (
                  <button
                    onClick={() => onPublishToSocial(
                      `${result.data.post || ''}\n\n${(result.data.hashtags || []).join(' ')}`,
                      result.image_url || undefined
                    )}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    <Send size={16} />
                    Publikuj na Social Media
                  </button>
                )}

                <a
                  href={`/social-media`}
                  className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                >
                  <ExternalLink size={16} />
                  Otwórz Social Media Manager
                </a>

                <button
                  onClick={generate}
                  disabled={generating}
                  className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
                >
                  {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  Regeneruj
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <span className="text-xs text-gray-400">AI Marketing Studio — Kraków Business Hub</span>
          <button onClick={onClose} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] text-sm font-medium">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ Visualization Components ═══

function SocialPostPreview({ data, imageUrl }: { data: any; imageUrl: string | null }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden max-w-md mx-auto">
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#0f2a4d] to-[#d4a017] rounded-full flex items-center justify-center text-white font-bold text-sm">FPN</div>
          <div>
            <p className="text-sm font-semibold">Fundacja Promocji Nowej Huty</p>
            <p className="text-xs text-gray-400">{data.best_time || 'Teraz'}</p>
          </div>
        </div>
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{data.post}</p>
      </div>
      {imageUrl && <img src={imageUrl} alt="" className="w-full" />}
      <div className="px-4 py-2 border-t">
        <p className="text-xs text-blue-600">{(data.hashtags || []).join(' ')}</p>
      </div>
      {data.media_suggestion && (
        <div className="px-4 py-2 border-t bg-amber-50">
          <p className="text-xs text-amber-700"><ImageIcon size={12} className="inline mr-1" />Sugestia: {data.media_suggestion}</p>
        </div>
      )}
    </div>
  );
}

function FlyerPreview({ data, imageUrl }: { data: any; imageUrl: string | null }) {
  return (
    <div className="bg-gradient-to-b from-[#0f2a4d] to-[#1a3d6e] text-white rounded-xl p-8 max-w-sm mx-auto shadow-xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4a017] rounded-bl-full opacity-20" />
      <h2 className="text-2xl font-black mb-2 relative z-10">{data.headline}</h2>
      <p className="text-blue-200 text-sm mb-6 relative z-10">{data.subtitle}</p>
      <ul className="space-y-2 mb-6 relative z-10">
        {(data.benefits || []).map((b: string, i: number) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="text-[#d4a017] mt-0.5">&#10003;</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="bg-[#d4a017] text-[#0f2a4d] rounded-lg px-4 py-3 text-center font-bold text-sm relative z-10">
        {data.cta}
      </div>
      {data.contact && <p className="text-xs text-blue-300 text-center mt-3 relative z-10">{data.contact}</p>}
      {data.color_scheme && <p className="text-xs text-blue-400 mt-2">Schemat: {data.color_scheme}</p>}
    </div>
  );
}

function OfferPreview({ data }: { data: any }) {
  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6 space-y-4 max-w-lg mx-auto">
      <div className="border-b pb-4">
        <h2 className="text-xl font-bold text-[#0f2a4d]">{data.title}</h2>
        <p className="text-sm text-gray-500 mt-1">Fundacja Promocji Nowej Huty | krakow.business</p>
      </div>
      <p className="text-sm text-gray-700">{data.intro}</p>
      <div>
        <h3 className="font-semibold text-sm text-gray-800 mb-2">Zakres usług:</h3>
        <ul className="space-y-1.5">
          {(data.scope || []).map((s: string, i: number) => (
            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="bg-[#0f2a4d] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
              {s}
            </li>
          ))}
        </ul>
      </div>
      {data.pricing && <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200"><p className="text-sm font-medium text-emerald-800">{data.pricing}</p></div>}
      {data.terms && <p className="text-xs text-gray-500">{data.terms}</p>}
      <div className="bg-[#0f2a4d] text-white rounded-lg px-4 py-3 text-center font-medium text-sm">{data.cta}</div>
      {data.validity && <p className="text-xs text-gray-400 text-center">Ważność oferty: {data.validity}</p>}
    </div>
  );
}

function EmailPreview({ data }: { data: any }) {
  return (
    <div className="bg-white rounded-lg border shadow-sm max-w-lg mx-auto overflow-hidden">
      <div className="bg-gray-100 px-4 py-2 border-b">
        <p className="text-xs text-gray-500">Temat: <strong className="text-gray-800">{data.subject}</strong></p>
        {data.preheader && <p className="text-xs text-gray-400 mt-0.5">{data.preheader}</p>}
      </div>
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-bold text-[#0f2a4d]">{data.headline}</h2>
        {(data.body_paragraphs || []).map((p: string, i: number) => (
          <p key={i} className="text-sm text-gray-700 leading-relaxed">{p}</p>
        ))}
        <div className="text-center pt-2">
          <span className="inline-block bg-[#d4a017] text-[#0f2a4d] font-bold px-6 py-3 rounded-lg text-sm">
            {data.cta_text}
          </span>
        </div>
      </div>
      {data.footer && <div className="bg-gray-50 px-6 py-3 border-t"><p className="text-xs text-gray-500">{data.footer}</p></div>}
    </div>
  );
}

function CaseStudyPreview({ data }: { data: any }) {
  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <h2 className="text-lg font-bold text-[#0f2a4d]">{data.title}</h2>
      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
        <h3 className="text-sm font-bold text-red-800 mb-1">Wyzwanie</h3>
        <p className="text-sm text-red-700">{data.challenge}</p>
      </div>
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="text-sm font-bold text-blue-800 mb-1">Rozwiązanie</h3>
        <p className="text-sm text-blue-700">{data.solution}</p>
      </div>
      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
        <h3 className="text-sm font-bold text-emerald-800 mb-1">Rezultaty</h3>
        <ul className="space-y-1">{(data.results || []).map((r: string, i: number) => <li key={i} className="text-sm text-emerald-700">&#10003; {r}</li>)}</ul>
      </div>
      {data.metrics && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-lg p-3 text-center"><p className="text-xs text-gray-500">Przed</p><p className="font-bold text-red-600">{data.metrics.before}</p></div>
          <div className="bg-gray-100 rounded-lg p-3 text-center"><p className="text-xs text-gray-500">Po</p><p className="font-bold text-emerald-600">{data.metrics.after}</p></div>
        </div>
      )}
      {data.quote && (
        <blockquote className="border-l-4 border-[#d4a017] pl-4 py-2 bg-amber-50 rounded-r-lg">
          <p className="text-sm italic text-gray-700">"{data.quote}"</p>
          <p className="text-xs text-gray-500 mt-1">— {data.quote_author}</p>
        </blockquote>
      )}
    </div>
  );
}

function GoogleAdPreview({ data }: { data: any }) {
  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {(data.variants || []).map((v: any, i: number) => (
        <div key={i} className="bg-white rounded-lg border p-4">
          <p className="text-xs text-gray-400 mb-2">Wariant {i + 1}</p>
          <p className="text-blue-700 text-base font-medium hover:underline cursor-pointer">{v.h1} | {v.h2} | {v.h3}</p>
          <p className="text-xs text-green-700 mb-1">krakow.business/{v.path || ''}</p>
          <p className="text-sm text-gray-600">{v.d1}</p>
          <p className="text-sm text-gray-600">{v.d2}</p>
        </div>
      ))}
    </div>
  );
}

function NewsletterPreview({ data }: { data: any }) {
  return (
    <div className="bg-white rounded-lg border p-6 max-w-lg mx-auto">
      <h2 className="text-lg font-bold text-[#0f2a4d] mb-2">{data.headline}</h2>
      <p className="text-sm text-gray-800 font-medium mb-3">{data.lead}</p>
      <p className="text-sm text-gray-600 leading-relaxed mb-4">{data.body}</p>
      <span className="inline-block bg-[#0f2a4d] text-white px-4 py-2 rounded text-sm font-medium">{data.cta_text}</span>
      {data.image_suggestion && <p className="text-xs text-gray-400 mt-3"><ImageIcon size={12} className="inline mr-1" />{data.image_suggestion}</p>}
    </div>
  );
}
